import { useEffect, useRef } from 'react';

/** Options for the useIdleTimeout hook. */
export interface IdleTimeoutOptions {
  /** Milliseconds of inactivity before onTimeout fires (e.g. 180_000 = 3 min). */
  timeoutMs: number;
  /** Callback invoked once when the idle window expires. */
  onTimeout: () => void;
}

/**
 * Invisible inactivity watchdog hook.
 *
 * Registers DOM event listeners on `window` for a set of user-presence signals.
 * Any tracked event resets the countdown to `timeoutMs`. When the full interval
 * elapses without any tracked event, `onTimeout` is called exactly once.
 *
 * Design constraints:
 * - `onTimeout` is stored in a ref so a changing callback reference (e.g. a
 *   freshly-created arrow function on each render) never triggers effect teardown
 *   and re-registration of listeners.
 * - mousemove and scroll are high-frequency; a 200 ms ref-throttle prevents
 *   excessive clearTimeout/setTimeout thrashing.
 * - Passive listeners for scroll and touchstart avoid blocking the main thread.
 * - StrictMode-safe: the effect cleanup removes the exact same function
 *   references that were registered, so a double-invoke (mount→unmount→mount)
 *   leaves exactly one set of listeners active.
 */
export function useIdleTimeout({ timeoutMs, onTimeout }: IdleTimeoutOptions): void {
  // Keep the latest onTimeout without requiring the effect to re-run when it changes.
  const onTimeoutRef = useRef(onTimeout);
  // Direct assignment in render (no useEffect wrapper) is the canonical React pattern for
  // "stable event handler" — safe because it's synchronous and does not mutate DOM/state.
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    const THROTTLE_MS = 200;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let lastReset = 0; // epoch ms; Date.now() on any platform always > 0

    /** (Re)start the idle countdown. */
    function schedule(): void {
      clearTimeout(timer);
      timer = setTimeout(() => {
        onTimeoutRef.current();
      }, timeoutMs);
    }

    /**
     * Throttled activity handler.
     * High-frequency events (mousemove, scroll) may fire 60×/s — only allow a
     * timer reset if at least THROTTLE_MS ms have elapsed since the last reset.
     * Low-frequency events (click, keydown, touchstart) also pass through this
     * gate, though in practice they never fire fast enough to be throttled.
     */
    function handleActivity(): void {
      const now = Date.now();
      if (now - lastReset < THROTTLE_MS) return;
      lastReset = now;
      schedule();
    }

    // Start the countdown immediately on mount.
    schedule();

    // Register all presence-signal events.
    // scroll and touchstart use { passive: true } to avoid blocking the event loop.
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('scroll', handleActivity, { passive: true });
    window.addEventListener('touchstart', handleActivity, { passive: true });

    return () => {
      // Clear the pending timer so it does not fire after unmount.
      clearTimeout(timer);
      // Remove all listeners using the same handleActivity reference that was
      // registered — this is what makes StrictMode safe (the cleanup always
      // removes what the corresponding mount registered).
      // Note: removeEventListener ignores the `passive` option for matching;
      // only `capture` matters, so passing no options here is correct.
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
    };
  }, [timeoutMs]);
}
